import React, { useEffect } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button, Form } from "react-bootstrap";
import DatePicker from "react-date-picker";
import { toast } from "react-toastify";
import usePatient from "../../../Hooks/usePatient";
import CustomModal from "../../../components/customModal/CustomModal";
import {
  AddPatientFormInitialValues,
  genderOptions,
} from "../EcwConstants";
import { useModulePermissions, PermissionModule, PermissionIdentifier } from "../../../utils/permissionUtils";

interface EcwAddPatientModalProps {
  show: boolean;
  onClose: () => void;
  onSuccess: (patientRes: any) => void;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

const patientValidationSchema = Yup.object({
  firstName: Yup.string().required("First Name is Required"),
  lastName: Yup.string().required("Last Name is Required"),
  patientCode: Yup.string().required("Patient ID is Required"),
  dob: Yup.date().nullable(),
  gender: Yup.string().required("Gender is required"),
});

const EcwAddPatientModal: React.FC<EcwAddPatientModalProps> = ({
  show,
  onClose,
  onSuccess,
  setLoading,
}) => {
  const { createPatientAPI, createPatientLoading } = usePatient();
  const isPermitted = useModulePermissions(PermissionModule.ECW_ORDER_ADMINISTRATION);
  const isAddPatientPermitted = isPermitted(PermissionIdentifier.Add_PATIENT_ON_ECW);

  useEffect(() => {
    setLoading?.(createPatientLoading);
    return () => {
      setLoading?.(false);
    };
  }, [createPatientLoading, setLoading]);

  const patientForm = useFormik({
    initialValues: AddPatientFormInitialValues,
    validationSchema: patientValidationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const patientRes = await createPatientAPI({
          ...values,
          dob: values.dob ? values.dob.toISOString().split("T")[0] : "",
        });

        if (patientRes?.id) {
          toast.success("Patient added successfully");
          onSuccess(patientRes);
          resetForm();
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to add patient");
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (!show) return null;

  return (
    <CustomModal
      id="addpatientcmodal"
      title="Add Patient"
      content={
        <div className="add-patient-details">
          <Form noValidate onSubmit={patientForm.handleSubmit}>
            <Form.Group className="form-field">
              <Form.Label>
                Patient ID<sup>*</sup>
              </Form.Label>
              <Form.Control
                id="patientCode"
                name="patientCode"
                type="text"
                autoFocus
                placeholder="Enter Patient ID"
                onChange={patientForm.handleChange}
                value={patientForm.values.patientCode}
                isInvalid={
                  patientForm.touched.patientCode &&
                  !!patientForm.errors.patientCode
                }
              />
              {patientForm.touched.patientCode &&
                patientForm.errors.patientCode ? (
                <Form.Control.Feedback type="invalid">
                  {patientForm.errors.patientCode}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field">
              <Form.Label>
                First Name<sup>*</sup>
              </Form.Label>
              <Form.Control
                id="firstName"
                name="firstName"
                type="text"
                placeholder="Enter First Name"
                onChange={patientForm.handleChange}
                value={patientForm.values.firstName}
                isInvalid={
                  patientForm.touched.firstName &&
                  !!patientForm.errors.firstName
                }
              />
              {patientForm.touched.firstName &&
                patientForm.errors.firstName ? (
                <Form.Control.Feedback type="invalid">
                  {patientForm.errors.firstName}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field">
              <Form.Label>
                Last Name<sup>*</sup>
              </Form.Label>
              <Form.Control
                id="lastName"
                name="lastName"
                type="text"
                placeholder="Enter Last Name"
                onChange={patientForm.handleChange}
                value={patientForm.values.lastName}
                isInvalid={
                  patientForm.touched.lastName &&
                  !!patientForm.errors.lastName
                }
              />
              {patientForm.touched.lastName &&
                patientForm.errors.lastName ? (
                <Form.Control.Feedback type="invalid">
                  {patientForm.errors.lastName}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field">
              <Form.Label>DOB</Form.Label>
              <div className="calender-box">
                <DatePicker
                  locale="en-US"
                  format="MM/dd/yyyy"
                  dayPlaceholder="dd"
                  monthPlaceholder="mm"
                  yearPlaceholder="yyyy"
                  clearIcon={null}
                  showLeadingZeros={true}
                  openCalendarOnFocus={false}
                  value={patientForm.values.dob}
                  calendarIcon={<i className="bi bi-calendar"></i>}
                  onChange={(date) =>
                    patientForm.setFieldValue("dob", date)
                  }
                  className={`${patientForm.touched.dob && !!patientForm.errors.dob ? "is-invalid-border" : ""} form-control`}
                />
              </div>
              {patientForm.touched.dob && patientForm.errors.dob ? (
                <div className="invalid-feedback d-block">
                  {patientForm.errors.dob}
                </div>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field">
              <Form.Label>
                Gender<sup>*</sup>
              </Form.Label>
              <div className="item-radio-content">
                {genderOptions.map((gender) => (
                  <Form.Check
                    key={gender}
                    inline
                    type="radio"
                    id={`gender-${gender}`}
                    name="gender"
                    label={gender}
                    value={gender}
                    onChange={patientForm.handleChange}
                    checked={patientForm.values.gender === gender}
                    isInvalid={
                      patientForm.touched.gender &&
                      !!patientForm.errors.gender
                    }
                  />
                ))}
                {patientForm.touched.gender && patientForm.errors.gender ? (
                  <div className="invalid-feedback d-block">
                    {patientForm.errors.gender}
                  </div>
                ) : null}
              </div>
            </Form.Group>
            <div className="form-actions">
              <Button
                type="button"
                variant="outline-primary"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={patientForm.isSubmitting || !isAddPatientPermitted}>
                Add
              </Button>
            </div>
          </Form>
        </div>
      }
      onClose={onClose}
    />
  );
};

export default EcwAddPatientModal;
